/**
 * ScoreBird Poller — polls the server proxy for live NeST game state
 * and pushes updates into the dataStore as a data source.
 *
 * Each project item can have one ScoreBird connection.
 * The poller auto-maps ScoreBird fields to matching template field IDs.
 */
import { dataStore } from '../stores/dataStore';
import { projectStore } from '../stores/projectStore';
import { templateRegistry } from './templateRegistry';
import { eventBus } from './eventBus';

const POLL_INTERVAL_MS = 2500;

/**
 * Fallback NeST unit for trainees whose own ScoreBird has not shipped yet.
 * A real, always-online football unit — so the live-data lesson steps work for
 * everyone. Change it in this one place if the unit is ever retired.
 */
export const DEMO_NEST_ID = '20448';

interface ScoreBirdConnection {
  nestId: string;
  sport: string;
  projectItemId: string;
  dataSourceId: string;
  timerId: ReturnType<typeof setInterval> | null;
}

const connections = new Map<string, ScoreBirdConnection>(); // keyed by projectItemId

/** ScoreBird API response shape */
interface ScoreBirdResponse {
  success: boolean;
  data: Record<string, string>;
}

/**
 * Field name mapping: ScoreBird API key → our template field ID.
 * Keys not listed here pass through with camelCase conversion.
 */
const FIELD_MAP: Record<string, Record<string, string>> = {
  // Common across sports
  common: {
    'Home': 'homeScore',
    'Away': 'awayScore',
    'Time': 'clock',
    'Pos': 'possession',
    'Secondary Time': 'secondaryTime',
  },
  football: {
    'Home': 'homeScore',
    'Away': 'awayScore',
    'Time': 'clock',
    'Quarter': 'period',
    'Down': 'down',
    'To Go': 'distance',
    'Ball On': 'ballOn',
    'Pos': 'possession',
    'Home TOL': 'homeTimeouts',
    'Away TOL': 'awayTimeouts',
    'Flag': 'flag',
    'Secondary Time': 'playClock',
  },
  basketball: {
    'Home': 'homeScore',
    'Away': 'awayScore',
    'Time': 'clock',
    'Quarter': 'period',
    'Pos': 'possession',
    'Home TOL': 'homeTimeouts',
    'Away TOL': 'awayTimeouts',
    'Home Fouls': 'homeFouls',
    'Away Fouls': 'awayFouls',
    'Home Bonus': 'homeBonus',
    'Home Dbl Bonus': 'homeDblBonus',
    'Away Bonus': 'awayBonus',
    'Away Dbl Bonus': 'awayDblBonus',
    'Secondary Time': 'shotClock',
  },
  baseball: {
    'Home': 'homeScore',
    'Away': 'awayScore',
    'Inning': 'inning',
    'Top/Bottom': 'halfInning',
    'Balls': 'balls',
    'Strikes': 'strikes',
    'Outs': 'outs',
    'Hit/Error': 'hitError',
  },
  soccer: {
    'Home': 'homeScore',
    'Away': 'awayScore',
    'Time': 'clock',
    'Half': 'period',
  },
  hockey: {
    'Home': 'homeScore',
    'Away': 'awayScore',
    'Time': 'clock',
    'Period': 'period',
    'Home Penalty': 'homePenalty',
    'Away Penalty': 'awayPenalty',
  },
  volleyball: {
    'Home': 'homeScore',
    'Away': 'awayScore',
    'Set': 'period',
    'Home Sets': 'homeSets',
    'Away Sets': 'awaySets',
  },
  wrestling: {
    'Home': 'homeScore',
    'Away': 'awayScore',
    'Time': 'clock',
    'Period': 'period',
  },
  lacrosse: {
    'Home': 'homeScore',
    'Away': 'awayScore',
    'Time': 'clock',
    'Quarter': 'period',
  },
};

/**
 * Normalize ScoreBird API data into flat key-value pairs using our field IDs.
 */
function normalizeData(rawData: Record<string, string>, sport: string): Record<string, string> {
  const sportMap = FIELD_MAP[sport] || FIELD_MAP.common;
  const result: Record<string, string> = {};

  for (const [key, value] of Object.entries(rawData)) {
    // Skip metadata
    if (['device_serial', 'ts', 'sport', 'collected_at'].includes(key)) continue;

    const mappedKey = sportMap[key] || FIELD_MAP.common[key];
    if (mappedKey) {
      result[mappedKey] = String(value);
    } else {
      // Pass through with camelCase conversion: "Home Fouls" → "homeFouls"
      const camel = key
        .split(' ')
        .map((w, i) => i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join('');
      result[camel] = String(value);
    }
  }

  return result;
}

/**
 * Connect a project item to a ScoreBird NeST device.
 * Creates a data source and starts polling.
 */
export function connectScorebird(projectItemId: string, nestId: string, sport: string): void {
  // Disconnect existing connection for this item
  disconnectScorebird(projectItemId);

  // Create data source
  const source = dataStore.getState().addSource({
    name: `ScoreBird #${nestId} (${sport})`,
    type: 'scorebird' as any,
    columns: [],
    rows: [{}],
    activeRowIndex: 0,
  });

  const conn: ScoreBirdConnection = {
    nestId,
    sport,
    projectItemId,
    dataSourceId: source.id,
    timerId: null,
  };

  connections.set(projectItemId, conn);

  // Auto-link: create links for any template field that matches a ScoreBird field
  autoLinkFields(projectItemId, source.id, sport);

  // Start polling
  poll(conn); // immediate first poll
  conn.timerId = setInterval(() => poll(conn), POLL_INTERVAL_MS);

  console.debug('[ScoreBird] Connected item', projectItemId, 'to NeST', nestId, sport);
  // Note: data:sourceCreated already fired by dataStore.addSource() above.
}

/**
 * Disconnect a project item from ScoreBird.
 */
export function disconnectScorebird(projectItemId: string): void {
  const conn = connections.get(projectItemId);
  if (!conn) return;

  if (conn.timerId) clearInterval(conn.timerId);
  dataStore.getState().removeSource(conn.dataSourceId);
  connections.delete(projectItemId);
  console.debug('[ScoreBird] Disconnected item', projectItemId);
}

/**
 * Get the active ScoreBird connection for a project item (if any).
 */
export function getConnection(projectItemId: string): { nestId: string; sport: string } | null {
  const conn = connections.get(projectItemId);
  return conn ? { nestId: conn.nestId, sport: conn.sport } : null;
}

/**
 * Template field ids that carry the same value as a differently-named ScoreBird field.
 * Keyed by template field id, valued by the normalized ScoreBird name.
 *
 * `quarter`: the football templates label the period box "Quarter", but the poller
 * normalizes ScoreBird's "Quarter" key to `period`. Without this the quarter box
 * sits at its default while every other field goes live.
 */
const FIELD_ALIASES: Record<string, string> = {
  quarter: 'period',
};

/**
 * Auto-create data links for all template fields that match ScoreBird field names.
 */
function autoLinkFields(projectItemId: string, dataSourceId: string, sport: string): void {
  const item = projectStore.getState().getItem(projectItemId);
  if (!item?.templateId) return;

  const template = templateRegistry.getTemplate(item.templateId);
  if (!template) return;

  // Build a set of all possible normalized field names for this sport
  const sportMap = FIELD_MAP[sport] || FIELD_MAP.common;
  const allMappedValues = new Set([
    ...Object.values(sportMap),
    ...Object.values(FIELD_MAP.common),
  ]);

  for (const field of template.fields) {
    // Link on an exact name match, or via an alias when the template uses a
    // sport-specific label for the same value.
    const columnName = allMappedValues.has(field.id) ? field.id : FIELD_ALIASES[field.id];
    if (columnName && allMappedValues.has(columnName)) {
      dataStore.getState().addLink({
        dataSourceId,
        columnName,
        projectItemId,
        fieldId: field.id,
      });
    }
  }
}

/**
 * Poll the ScoreBird proxy and update the data source.
 */
async function poll(conn: ScoreBirdConnection): Promise<void> {
  try {
    const resp = await fetch(`/api/v1/scorebird/game-state?nestId=${encodeURIComponent(conn.nestId)}`);
    if (!resp.ok) {
      console.warn('[ScoreBird] Poll failed:', resp.status);
      return;
    }

    const json: ScoreBirdResponse = await resp.json();
    if (!json.success || !json.data) {
      console.warn('[ScoreBird] API returned success=false or no data');
      return;
    }

    const normalized = normalizeData(json.data, conn.sport);
    const columns = Object.keys(normalized);

    // Update the data source with new values
    const sources = dataStore.getState().sources;
    const source = sources.find((s) => s.id === conn.dataSourceId);
    if (!source) return;

    // Check if data actually changed
    const currentRow = source.rows[0] || {};
    let changed = false;
    for (const [k, v] of Object.entries(normalized)) {
      if (currentRow[k] !== v) {
        changed = true;
        break;
      }
    }

    if (!changed && source.columns.length > 0) return; // no change, skip update

    // Update source columns and row
    dataStore.setState({
      sources: dataStore.getState().sources.map((s) =>
        s.id === conn.dataSourceId
          ? { ...s, columns, rows: [normalized] }
          : s
      ),
    });

    // Trigger field updates via the data linker
    eventBus.emit('data:rowChanged', { sourceId: conn.dataSourceId, rowIndex: 0 });
  } catch (err: any) {
    console.warn('[ScoreBird] Poll error:', err.message);
  }
}

/**
 * Get all active connections (for UI display).
 */
export function getActiveConnections(): Array<{ projectItemId: string; nestId: string; sport: string }> {
  return Array.from(connections.values()).map((c) => ({
    projectItemId: c.projectItemId,
    nestId: c.nestId,
    sport: c.sport,
  }));
}
